<template>
  <div
    class="main-menu menu-fixed menu-accordion menu-shadow"
    :class="[
      { 'expanded': !isVerticalMenuCollapsed || (isVerticalMenuCollapsed && isMouseHovered) },
      skin === 'semi-dark' ? 'menu-dark' : 'menu-light'
    ]"
    @mouseenter="updateMouseHovered(true)"
    @mouseleave="updateMouseHovered(false)"
  >
    <!-- main menu header-->
    <div class="expanded">
      <slot
        name="header"
        :toggleVerticalMenuActive="toggleVerticalMenuActive"
        :toggleCollapsed="toggleCollapsed"
        :collapseTogglerIcon="collapseTogglerIcon"
      >
        <ul class="nav navbar-nav flex-row">

          <!-- Logo & Text -->
          <li class="w-100">
            <b-link
              class="w-100"
              to="/"
            >
              <span class="brand-logo-vertical-nav-menu" :style="bg_color_main_navbar">
                <b-img
                  v-if="checkOutHome()"
                  fluid
                  src="@/assets/images/logo/ooh.avif"
                  alt="Logo"
                  style="max-width: 100px"
                />
                <b-img
                  v-else
                  :src="is_whitelabel_and_has_logo ? verifyAsset(user_data.whitelabel_settings.logo) : require('@/assets/images/logo/logo-brandme.png')"
                  alt="Logo"
                  style="max-width: 100px; max-height: 43px"
                  :key="update_image"
                  @error="handleImageError"
                />

                <b-link class="menu-toggle-close-icon">
                  <feather-icon
                    icon="XIcon"
                    size="20"
                    class=""
                    @click="toggleVerticalMenuActive"
                  />
                </b-link>
              </span>
            </b-link>
          </li>
        </ul>
      </slot>
    </div>
    <!-- / main menu header-->

    <!-- Shadow -->
    <div
      :class="{'d-block': shallShadowBottom}"
      class="shadow-bottom"
    />

    <!-- main menu content-->
    <vue-perfect-scrollbar
      :settings="perfectScrollbarSettings"
      class="main-menu-content scroll-area"
      tagname="ul"
      @ps-scroll-y="evt => { shallShadowBottom = evt.srcElement.scrollTop > 0 }"
    >
      <vertical-nav-menu-items
        :items="navMenuItems"
        class="navigation navigation-main"
      />
    </vue-perfect-scrollbar>
    <!-- /main menu content-->
  </div>
</template>

<script>
import navMenuItems from '@/navigation/vertical'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import { BLink, BImg } from 'bootstrap-vue'
import { provide, computed, ref, onMounted } from '@vue/composition-api'
import useAppConfig from '@core/app-config/useAppConfig'
import { $themeConfig } from '@themeConfig'
import VerticalNavMenuItems from './components/vertical-nav-menu-items/VerticalNavMenuItems.vue'
import useVerticalNavMenu from './useVerticalNavMenu';
import { getUserData, updateWhitelabelSettings } from '@/libs/utils/user';
import { verifyAsset } from '@/libs/utils/urls';
import { getLogo } from '@/services/settings';


export default {
  components: {
    VuePerfectScrollbar,
    VerticalNavMenuItems,
    BLink,
    BImg,
  },
  props: {
    isVerticalMenuActive: {
      type: Boolean,
      required: true,
    },
    toggleVerticalMenuActive: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      verifyAsset,
      user_data: getUserData(),
      domain_route: window.location.host,
      update_image: false,
    }
  },
  computed: {
    is_whitelabel_and_has_logo() {
      return this.user_data?.whitelabel_settings?.logo || false;
    },
    bg_color_main_navbar() {
      if (this.domain_route.includes('outofhome')) return 'background: #e74353'
      else return 'background: var(--header-bg);';
    }
  },
  setup(props) {
    const {
      isMouseHovered,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      updateMouseHovered,
    } = useVerticalNavMenu(props)

    const TALENTOS_ITEM = {
      title: 'talentos',
      img_svg: 'proposals_black',
      route: 'talentos',
      resource: 'Creator',
      action: 'manage',
      newLabel: false,
      disabled: false,
    }

    const menuItems = ref([...navMenuItems])

    const swapProposalsForTalentos = () => {
      const user = getUserData()
      const isCreator = user && user.main_group && user.main_group.name === 'creator'
      if (!isCreator) return
      const hasAgency = (user.user_permissions || []).some(p => p.codename === 'user_agency')
      if (!hasAgency) return
      menuItems.value = menuItems.value.map(item => { return item.route === 'view_proposals_edit' ? TALENTOS_ITEM : item })
    }

    provide('isMouseHovered', isMouseHovered)

    const { skin } = useAppConfig()

    // Shadow bottom is UI specific and can be removed by user => It's not in `useVerticalNavMenu`
    const shallShadowBottom = ref(false)

    const perfectScrollbarSettings = {
      maxScrollbarLength: 60,
      wheelPropagation: false,
    }

    const collapseTogglerIconFeather = computed(() => (collapseTogglerIcon.value === 'unpinned' ? 'CircleIcon' : 'DiscIcon'))

    // App Name
    const { appName, appLogoImage } = $themeConfig.app

    onMounted(() => {
      swapProposalsForTalentos()
    })

    return {
      navMenuItems: menuItems,
      perfectScrollbarSettings,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      isMouseHovered,
      updateMouseHovered,
      collapseTogglerIconFeather,

      // Shadow Bottom
      shallShadowBottom,

      // Skin
      skin,

      // App Name
      appName,
      appLogoImage,
    }
  },
  methods: {
    checkOutHome() {
      return this.domain_route.includes('outofhome')
    },
    handleImageError(event) {
      getLogo().then(response => {
        this.user_data.whitelabel_settings.logo = response.logo_url;
        updateWhitelabelSettings(this.user_data.whitelabel_settings);
        event.target.src = response.logo_url;
      }).catch(() => {
        event.target.src = require('@/assets/images/logo/logo-brandme.png');
      });
    },
  },
}
</script>

<style lang="scss">
@import "~@core/scss/base/core/menu/menu-types/vertical-menu.scss";
</style>
<style scoped lang="scss">
.menu-toggle-close-icon {
  position: absolute;
  top: 10px;
  right: 10px;
  cursor: pointer;
  color: var(--header-text) !important;
  border-radius: 100%;
  &:hover {
    background-color: var(--header-text-hover);
  }
}
.brand-logo-vertical-nav-menu {
  width: 100% !important;
  height: 100px !important;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  box-shadow: inset 0 0 0 1px rgba(0,0,0,0.06);
  margin-bottom: 0.6em;

  &img {
    width: 100%;
    height: auto;
    object-fit: contain;
  }
}
</style>